import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { MODULES_REGISTRY, type AccessRole } from "@/core/navigation/modulesRegistry";
import { CampoPesquisa } from "@/shared/components/CampoPesquisa";
import { compararPtBr, normalizarBusca } from "@/shared/lib/busca";

/** Descrição curta de cada módulo (mesmo texto que já existia no hub da Início). */
const DESCRICAO: Record<string, string> = {
  biblioteca: "Explore o acervo e acompanhe seus empréstimos.",
  eventos: "Programações e atividades da igreja.",
  reservas: "Reserve salas e ambientes, sem conflitos.",
  escala: "Acompanhe suas escalas e confirme sua participação.",
  comunicados: "Comunicados da igreja, em um só lugar.",
  visitantes: "Cadastre quem visitou a igreja e acompanhe cada pessoa com carinho.",
};

const CARD_CLASSE =
  "group flex items-start gap-4 rounded-2xl border border-border bg-surface p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md";

/**
 * Aba "Módulos" da barra fixa inferior (Sprint 3 — redesign Elos). Lista os
 * módulos ativos e visíveis para a pessoa — o mesmo conteúdo que antes vivia
 * na seção "Uso dos módulos" da Início, agora com tela própria (a Início
 * virou só preview, sem atalhos de navegação — pedido do usuário).
 */
export function Modulos() {
  const { ehAdminPlataforma, podeGerenciarBiblioteca, temAcessoAdministrativo, modulosVisiveis } =
    useAuth();
  const navigate = useNavigate();
  const [busca, setBusca] = useState("");

  const role: AccessRole = { ehAdminPlataforma, podeGerenciarBiblioteca, temAcessoAdministrativo };
  // Ordem alfabética (A→Z, pt-BR) pelo nome exibido, qualquer que seja a ordem em
  // que o registry declara os módulos (o `order` do registry não é alterado).
  const ativos = useMemo(
    () =>
      MODULES_REGISTRY.filter((m) => modulosVisiveis.includes(m.code)).sort((a, b) =>
        compararPtBr(a.label, b.label),
      ),
    [modulosVisiveis],
  );
  const termo = normalizarBusca(busca.trim());
  const elosExibidos = termo
    ? ativos.filter((m) => normalizarBusca(m.label).includes(termo))
    : ativos;

  return (
    <main className="mx-auto w-full max-w-4xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
      <header className="mb-6">
        <h1 className="text-2xl font-black tracking-tight text-primary sm:text-3xl">Central Elos</h1>
        <p className="mt-1 text-sm text-muted">O que sua igreja ativou para você usar.</p>
      </header>

      {ativos.length > 0 && (
        <CampoPesquisa
          valor={busca}
          onChange={setBusca}
          placeholder="Pesquisar Elo..."
          rotulo="Pesquisar Elo"
          className="mb-4"
        />
      )}

      {!ativos.length ? (
        <p className="rounded-xl border border-border bg-surface p-6 text-sm text-muted">
          Nenhum elo disponível para você no momento.
        </p>
      ) : !elosExibidos.length ? (
        <p className="rounded-xl border border-border bg-surface p-6 text-sm text-muted">
          Nenhum elo encontrado para &quot;{busca.trim()}&quot;.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {elosExibidos.map((m) => {
            const Icone = m.icon;
            return (
              <button
                key={m.code}
                type="button"
                onClick={() => navigate(m.resolvePath(role))}
                className={CARD_CLASSE}
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                  <Icone size={24} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-bold text-text">{m.label}</span>
                  <span className="mt-1 block text-sm text-muted">{DESCRICAO[m.code] ?? ""}</span>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary">
                    Abrir
                    <ChevronRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </main>
  );
}
